import React, { useEffect, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import SubUserRequestModel from "../../../models/company/subUser/request/SubUserRequestModel";
import { OBJECT_NAME_RULE, EMAIL_RULE, FORM_SELECT_RULE } from "../../../utils/validation-utils";
import IdNameModel from "../../../models/common/IdNameModel";
import { RoleEnum } from "../../../models/enums/RoleEnum";
import toasterUtils from "../../../utils/toaster-utils";
import { GuidEnum } from "../../../models/enums/GuidEnum";
import { TextField, Select, MenuItem, Button, FormControl, InputLabel, FormHelperText, Autocomplete } from "@mui/material";

interface SubUserProps {
    objSubUser: SubUserRequestModel;
    actionType: string;
    roles: IdNameModel[];
    departments: IdNameModel[];
    cities: IdNameModel[];
    clientLeads: IdNameModel[];
    onCloseSubUser: () => void;
    onSubUserSave: (objSubUser: SubUserRequestModel) => void;
}

const SubUserCreateComponent = (props: SubUserProps) => {
    const {
        register,
        setValue,
        control,
        handleSubmit,
        formState: { errors, isValid },
    } = useForm<SubUserRequestModel>({
        mode: "onChange",
        defaultValues: props.objSubUser
    });

    const roleSelected = useWatch({
        name: "roleId",
        control: control,
        defaultValue: props.objSubUser ? props.objSubUser.roleId : RoleEnum.ClientLead,
    });

    useEffect(() => {
        setValue("roleId", props.objSubUser.roleId);
    }, []);

    const onSubmit = (formData: SubUserRequestModel) => {
        if (!isValid) {
            toasterUtils.showError("Please fill all mandatory fields");
            return;
        }
        if (formData.roleId == RoleEnum.ClientRecruiter && (formData.reportingToId === null || formData.reportingToId === GuidEnum.Empty)) {
            toasterUtils.showError("Please Select Reporting To");
            return;
        }
        formData.userName = formData.emailId;
        props.onSubUserSave(formData);
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row">
                <div className="col-md-6">
                    <FormControl fullWidth margin="normal" error={!!errors.name}>
                        <TextField label="Name" size="small" {...register("name", OBJECT_NAME_RULE)} error={!!errors.name}
                            helperText={errors.name?.message} autoComplete="off" required />
                    </FormControl>
                </div>
                <div className="col-md-6">
                    <FormControl fullWidth margin="normal" error={!!errors.emailId}>
                        <TextField type="email" label="Email" size="small" {...register("emailId", EMAIL_RULE)} error={!!errors.emailId}
                            helperText={errors.emailId?.message} autoComplete="off" disabled={props.objSubUser.subUserId !== GuidEnum.Empty} required />
                    </FormControl>
                </div>
            </div>
            <div className="row">
                <div className="col-md-6">
                    <Controller name="roleId" control={control} defaultValue={0} rules={FORM_SELECT_RULE}
                        render={({ field }) => (
                            <FormControl fullWidth margin="normal" error={!!errors.roleId}>
                                <InputLabel required>User Type</InputLabel>
                                <Select {...field} label="User Type" size="small">
                                    <MenuItem value={0}>Select Role</MenuItem>
                                    {props.roles.map((type, index) => (
                                        <MenuItem key={index} value={type.id}>
                                            {type.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText>{errors.roleId?.message}</FormHelperText>
                            </FormControl>
                        )}
                    />
                </div>
                <div className="col-md-6">
                    {parseInt(roleSelected.toString()) === parseInt(RoleEnum.ClientRecruiter.toString()) &&
                        <Controller name="reportingToId" control={control} defaultValue={GuidEnum.Empty} rules={FORM_SELECT_RULE}
                            render={({ field }) => (
                                <Autocomplete {...field} value={props.clientLeads.find(x => x.guId === field.value) || null}
                                    onChange={(_, newValue) => field.onChange(newValue ? newValue.guId : GuidEnum.Empty)} options={props.clientLeads} getOptionLabel={(option) => option.name || ""}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Reporting To" size="small" required margin="normal" fullWidth error={!!errors.reportingToId} helperText={errors.reportingToId?.message} />
                                    )}
                                    isOptionEqualToValue={(option, value) => option.guId === value.guId}
                                />
                            )}
                        />
                    }
                </div>
            </div>
            <div className="row">
                <div className="col-md-6">
                    <Controller name="departmentId" control={control} defaultValue={GuidEnum.Empty} rules={FORM_SELECT_RULE}
                        render={({ field }) => (
                            <Autocomplete {...field} value={props.departments.find(x => x.guId === field.value) || null}
                                onChange={(_, newValue) => field.onChange(newValue ? newValue.guId : GuidEnum.Empty)} options={props.departments} getOptionLabel={(option) => option.name || ""}
                                renderInput={(params) => (
                                    <TextField {...params} label="Department" size="small" required margin="normal" fullWidth error={!!errors.departmentId} helperText={errors.departmentId?.message} />
                                )}
                                isOptionEqualToValue={(option, value) => option.guId === value.guId}
                            />
                        )}
                    />
                </div>
                <div className="col-md-6">
                    <Controller name="cityId" control={control} defaultValue={0} rules={FORM_SELECT_RULE}
                        render={({ field }) => (
                            <Autocomplete {...field} value={props.cities.find(x => x.id === field.value) || null}
                                onChange={(_, newValue) => field.onChange(newValue ? newValue.id : 0)} options={props.cities} getOptionLabel={(option) => option.name || ""}
                                renderInput={(params) => (
                                    <TextField {...params} label="Location" size="small" required margin="normal" fullWidth error={!!errors.cityId} helperText={errors.cityId?.message} />
                                )}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                            />
                        )}
                    />
                </div>
            </div>
            <div className="row">
                <div className="col-md-12 text-end mt-2">
                    <Button variant="contained" color="success" type="submit" disabled={!isValid}>
                        Save
                    </Button>
                    <Button variant="contained" color="error" className="m-l-xxs" type="button" onClick={props.onCloseSubUser}>
                        Close
                    </Button>
                </div>
            </div>
        </form>
    );
};
export default SubUserCreateComponent;